import { CartModal } from "components/CartModal/CartModal";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { Corporate } from "components/icons/Corporate";
import { SmallRightArrow } from "components/icons/SmallRightArrow";
import { TASK_SOURCE } from "constants/task";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useModal } from "hooks/useModal";
import { useQuery } from "hooks/useQuery";
import { useToast } from "hooks/useToast";
import { useDispatch } from "react-redux";
import {
  useGetAllUnlaunchedTasksQuery,
  useGetTaskSeasonsQuery,
  useLazyGetAllUnlaunchedTasksQuery,
} from "store/apis/filing";
import { taxHomePageApi, useAddAlarmsToCartMutation } from "store/apis/taxHome";
import { BaseTaskOverview } from "types/Models/task";
import { BackendError } from "types/utils/error";
import { ModalProps } from "types/utils/modal";
import { formatDate } from "utils/formatDate";

const EntityFilingCard = ({
  tasks,
  close,
  seasons,
  paymentPending,
  isFetching,
}: {
  tasks: BaseTaskOverview[];
  close: () => void;
  seasons: string[];
  paymentPending: number | undefined;
  isFetching: boolean;
}) => {
  const [
    addAlarmToCart,
    { isLoading: isAddingToCart, isSuccess: addedItemsToCart },
  ] = useAddAlarmsToCartMutation();
  const { uuid: groupId } = useCurrentGroupContext();
  const cartModal = useModal();
  const {
    alertToast,
    successToast,
    loading: loadingToast,
    dismiss: dismissToast,
  } = useToast();
  const dispatch = useDispatch();
  const [getAllUnlaunchedTasks] = useLazyGetAllUnlaunchedTasksQuery();
  const entity = tasks[0]?.entity;
  const entityId = entity.uuid;

  const uniqueSeasons = new Set();

  tasks.forEach((task) => {
    if (
      task.source === TASK_SOURCE.ALARMS ||
      task.source === TASK_SOURCE.CART
    ) {
      uniqueSeasons.add(task.season);
    }
  });

  const handleAddItemToCart = async () => {
    const loadingtoastId = loadingToast({
      title: "Please wait",
      message: "Adding items to your cart",
    });
    try {
      await addAlarmToCart({
        entityId,
        season: Array.from(uniqueSeasons).join(","),
      }).unwrap();
    } catch (error) {
      alertToast({ message: (error as BackendError)?.data?.error?.message });
    } finally {
      dismissToast(loadingtoastId);
    }
  };

  const handlePaymentSuccess = async () => {
    const loadingtoastId = loadingToast({
      title: "Launching filings",
      message: "Your filings are being created, please wait.",
    });
    close();
    const pollForTaskData = async (): Promise<void> => {
      const maxAttempts = 10;
      let attempts = 0;
      while (attempts < maxAttempts) {
        try {
          const tasks = await getAllUnlaunchedTasks({
            groupId: groupId || "",
            season: seasons.join(","),
          }).unwrap();

          const entityStillExists =
            tasks?.entities?.[entity.registered_name]?.length > 0;

          if (tasks && !entityStillExists) {
            dispatch(
              taxHomePageApi.util.invalidateTags(["UNLAUNCHED_TASK_LIST"])
            );
            return;
          }
        } catch (error) {
          alertToast({
            message: (error as BackendError).data?.error?.message,
          });
        }
        attempts++;
        if (attempts < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, 3000));
        }
      }
      throw new Error("Task creation polling timed out");
    };
    try {
      await pollForTaskData();
      successToast({
        title: "Filings have been launched.",
      });
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    } finally {
      dismissToast(loadingtoastId);
    }
  };

  const isAllItemsInCart = tasks.every(
    (task) => task.source === TASK_SOURCE.CART
  );

  const isAddingAndFetchingInProgress =
    isAddingToCart || (isFetching && addedItemsToCart);

  return (
    <div className="t-space-y-5 t-border t-border-solid t-border-neutral-10 t-p-4 t-rounded-md">
      <div className="t-flex t-gap-3 t-items-center t-justify-between">
        <div className="t-flex t-gap-3 t-items-center">
          <div className="t-w-10 t-h-10 t-rounded-full t-flex t-items-center t-justify-center  t-bg-surface-grey">
            <Corporate className="t-text-neutral" />
          </div>
          <div>
            <div className="t-text-subtitle t-text-text-100">
              {entity.registered_name}
            </div>
            <div className="t-text-body-sm t-text-neutral-40">
              {tasks.length} Filings required
            </div>
          </div>
        </div>
        <div className="t-italic t-text-body-sm t-text-text-30 t-font-light">
          Total Amount: ${paymentPending || 0}
        </div>
      </div>
      <ol className="t-pl-4 t-space-y-1.5 t-list-decimal">
        {tasks.map((task, index) => (
          <li
            className="t-text-body-sm t-text-text-60"
            key={task.cart_item_uuid || task.alarm_uuid || index}
          >
            <span className="t-flex t-flex-wrap t-gap-1">
              <span>
                {task.task_title} (Season {task.season})
                {task.deadline_date ? "," : ""}
              </span>
              {task.deadline_date && (
                <span>Deadline - {formatDate(task.deadline_date)}</span>
              )}
            </span>
          </li>
        ))}
      </ol>
      <div className="t-mt-1">
        {isAllItemsInCart ? (
          <Button customType="link" onClick={cartModal.open}>
            Review & checkout &nbsp; <SmallRightArrow className="t-size-4" />
          </Button>
        ) : (
          <Button
            customType="link"
            onClick={handleAddItemToCart}
            isLoading={isAddingAndFetchingInProgress}
          >
            Add to cart &nbsp; <SmallRightArrow className="t-size-4" />
          </Button>
        )}
      </div>

      {entity && (
        <CartModal
          open={cartModal.isOpen}
          onClose={cartModal.close}
          entityIdFromProps={entityId}
          groupIdFromProps={groupId}
          hideEntitySelector
          handlePaymentSuccess={handlePaymentSuccess}
          shouldRedirectToTask={false}
        />
      )}
    </div>
  );
};

export const PurchaseAllFiling = ({ isOpen, close }: ModalProps) => {
  const query = useQuery();
  const selectedSeasons = query.get("seasons");
  const { uuid: groupId } = useCurrentGroupContext();

  const {
    data: taskSeasons,
    isSuccess: taskSeasonsSuccess,
    isLoading: taskSeasonsLoading,
    isUninitialized: taskSeasonsUninitialized,
  } = useGetTaskSeasonsQuery(
    {
      groupId,
    },
    { skip: !groupId }
  );

  const seasonsString = selectedSeasons
    ? selectedSeasons.replace("all,", "")
    : taskSeasons?.seasons?.join(",") || null;

  const seasons = seasonsString ? seasonsString.split(",") : [];

  const {
    data: unlaunchedTasks,
    isFetching,
    isSuccess,
    isLoading,
    isUninitialized,
  } = useGetAllUnlaunchedTasksQuery(
    {
      groupId: groupId || "",
      season: seasonsString,
    },
    { skip: !groupId || !taskSeasonsSuccess }
  );

  const isDataLoading =
    isLoading ||
    taskSeasonsLoading ||
    taskSeasonsUninitialized ||
    isUninitialized;

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={close} modal={false}>
        <Modal.Content size="large" useCustomOverlay>
          <Modal.Header>
            <Modal.Title>Review and Purchase Filings</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body className="t-space-y-4">
            <Async.Root
              {...{
                isLoading: isDataLoading,
                isEmpty: false,
                isSuccess: isSuccess || taskSeasonsSuccess,
              }}
              renderOnLoad={
                <div className="t-space-y-3 t-h-full t-w-full">
                  <Skeleton className="t-h-10 t-w-full" />
                  <Skeleton className="t-h-10 t-w-full" />
                  <Skeleton className="t-h-10 t-w-full" />
                  <Skeleton className="t-h-10 t-w-full" />
                  <Skeleton className="t-h-10 t-w-full" />
                  <Skeleton className="t-h-10 t-w-full" />
                </div>
              }
            >
              <Async.Empty>
                <></>
              </Async.Empty>
              <Async.Success>
                {Object.keys(unlaunchedTasks?.entities || {})?.map(
                  (entityName, index) => {
                    const tasks = unlaunchedTasks?.entities[entityName];
                    const paymentPending =
                      unlaunchedTasks?.entity_totals[entityName];

                    if (!tasks) {
                      return null;
                    }

                    return (
                      <EntityFilingCard
                        tasks={tasks}
                        key={index}
                        close={close}
                        seasons={seasons}
                        paymentPending={paymentPending}
                        isFetching={isFetching}
                      />
                    );
                  }
                )}
              </Async.Success>
            </Async.Root>
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
    </>
  );
};
